import { useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getEquipmentCalendar, selectEquipmentCalendar, selectEquipmentCalendarLoading, selectEquipmentJournalNoteDates } from "store/boats"
import { getWeekDates } from "../utils/calendarHelpers"

const ensureAsset = (assetMap, assetId, assetNumber) => {
  if (!assetMap.has(assetId)) {
    assetMap.set(assetId, { id: assetId, number: assetNumber, bookings: [], bookingIds: new Set() })
  }
  return assetMap.get(assetId)
}

// Dedupe key is per (day, booking_id) rather than just booking_id: the API intentionally
// returns a multi-day booking in every calendar_grid day entry it spans, and each of those
// occurrences needs to render as its own pill on that day. Deduping by booking_id alone would
// keep only the first day's occurrence and drop the booking from the days it also spans.
const pushBooking = (asset, booking, dayDate, dayKey) => {
  const dedupeKey = `${dayKey}_${booking.booking_id}`
  if (!asset.bookingIds.has(dedupeKey)) {
    asset.bookingIds.add(dedupeKey)
    asset.bookings.push({
      id: booking.booking_id,
      uuid: booking.booking_uuid,
      date: dayDate,
      timeRange: booking.start_time,
      tripName: booking.trip_name,
      paymentStatus: booking.payment_status,
      color: booking.color
    })
  }
}

const processMonthDay = (assetMap, dayData) => {
  const dayDate = new Date(dayData.date)
  dayData.bookings?.forEach((booking) => {
    booking.assets?.forEach((assetInfo) => {
      const asset = ensureAsset(assetMap, assetInfo.asset_id, assetInfo.asset_number)
      pushBooking(asset, booking, dayDate, dayData.date)
    })
  })
}

const processWeekDay = (assetMap, dayData) => {
  const dayDate = new Date(dayData.date)
  dayData.assets?.forEach((assetData) => {
    const asset = ensureAsset(assetMap, assetData.asset_id, assetData.asset_number)
    assetData.bookings?.forEach((booking) => pushBooking(asset, booking, dayDate, dayData.date))
  })
}

/**
 * Parse time string (e.g., "04:15 PM") to time slot index
 * Time slots start at 12 AM (index 0) and go to 11 PM (index 23)
 * @param {string} timeStr - Time string in format "HH:MM AM/PM"
 * @returns {number|null} - Time slot index or null if out of range
 */
const parseTimeToSlotIndex = (timeStr) => {
  if (!timeStr) return null

  try {
    // Parse time string like "04:15 PM"
    const regex = /(\d{1,2}):(\d{2})\s*(AM|PM)/i
    const match = regex.exec(timeStr)
    if (!match) return null

    let hour = Number.parseInt(match[1], 10)
    const ampm = match[3].toUpperCase()

    // Convert to 24-hour format
    if (ampm === "PM" && hour !== 12) {
      hour += 12
    } else if (ampm === "AM" && hour === 12) {
      hour = 0
    }

    // Time slots now start at 12 AM (hour 0)
    // So slot index = hour itself (0-23)
    return hour
  } catch (error) {
    console.error("Error parsing time:", timeStr, error)
    return null
  }
}

/**
 * Custom hook for managing calendar data fetching and processing
 * @param {string} equipmentId - Equipment ID
 * @param {string} viewMode - Current view mode (day/week/month)
 * @param {Date} selectedDate - Currently selected date
 * @param {string} activeFilter - Active filter (all/with_bookings/without_bookings)
 * @param {string} selectedAsset - Selected asset ID or "all"
 * @param {number} refreshTrigger - Optional trigger to force data refresh (increment to refetch)
 * @returns {Object} - Calendar data, loading state, and total bookings count
 */
export const useCalendarData = (equipmentId, viewMode, selectedDate, activeFilter, selectedAsset, refreshTrigger = 0) => {
  const dispatch = useDispatch()
  const calendarData = useSelector(selectEquipmentCalendar)
  const loading = useSelector(selectEquipmentCalendarLoading)
  const journalNoteDates = useSelector(selectEquipmentJournalNoteDates)
  const prevParamsRef = useRef(null)

  // Memoize params to prevent double API calls when multiple dependencies change
  const apiParams = useMemo(() => {
    // Determine asset filter based on view mode
    let assetFilter
    if (viewMode === "month") {
      assetFilter = selectedAsset === "all" ? "all" : "single_asset"
    } else {
      assetFilter = activeFilter
    }

    // For week view, send the first day of the week the user sees, so the requested
    // range always matches the columns rendered by WeekView (Sun-start via getWeekDates)
    let dateToSend = selectedDate
    if (viewMode === "week") {
      dateToSend = getWeekDates(selectedDate)[0]
    }

    // Format date as YYYY-MM-DD using local timezone (not UTC)
    const year = dateToSend.getFullYear()
    const month = String(dateToSend.getMonth() + 1).padStart(2, "0")
    const day = String(dateToSend.getDate()).padStart(2, "0")
    const formattedDate = `${year}-${month}-${day}`

    // Build API params based on current state
    return {
      view_type: viewMode,
      date: formattedDate, // YYYY-MM-DD format in local timezone
      asset_filter: assetFilter,
      ...(viewMode === "month" && selectedAsset !== "all" && { asset_id: selectedAsset })
    }
  }, [viewMode, selectedDate, activeFilter, selectedAsset])

  // Fetch calendar data when parameters change or refreshTrigger is updated
  useEffect(() => {
    if (!equipmentId) return

    // Compare with previous params to avoid duplicate calls (but always refresh if refreshTrigger changed)
    const paramsString = JSON.stringify(apiParams)
    if (prevParamsRef.current === paramsString && refreshTrigger === 0) {
      return // Skip if params haven't changed and no refresh trigger
    }

    prevParamsRef.current = paramsString
    dispatch(getEquipmentCalendar(equipmentId, apiParams))
  }, [dispatch, equipmentId, apiParams, refreshTrigger])

  // Map API response to component data structure
  const assets = useMemo(() => {
    const viewType = calendarData?.data?.view_type || calendarData?.metadata?.view_type

    // Day view has a different structure: direct assets array (no calendar_grid)
    if (viewType === "day") {
      const assetsData = calendarData?.data?.assets || calendarData?.assets

      if (!assetsData) {
        return []
      }

      // Map day view assets directly
      const result = assetsData
        .map((assetData) => {
          // Use a Map to deduplicate bookings by booking_id
          const bookingsMap = new Map()

            ; (assetData.bookings || []).forEach((booking) => {
              // Only add if not already in map (prevents duplicates)
              if (!bookingsMap.has(booking.booking_id)) {
                // Parse start and end times to calculate startTime and duration
                const startTime = parseTimeToSlotIndex(booking.start_time)
                const endTime = parseTimeToSlotIndex(booking.end_time)
                const duration = endTime !== null && startTime !== null ? endTime - startTime : 1

                bookingsMap.set(booking.booking_id, {
                  id: booking.booking_id,
                  uuid: booking.booking_uuid,
                  date: new Date(booking.booked_date),
                  timeRange: booking.time_range,
                  tripName: booking.trip_name,
                  paymentStatus: booking.payment_status,
                  color: booking.color,
                  startTime, // Time slot index (0-23 for 12 AM - 11 PM)
                  duration // Duration in time slots
                })
              }
            })

          return {
            id: assetData.asset_id,
            number: assetData.asset_number,
            bookings: Array.from(bookingsMap.values())
          }
        })
        .sort((a, b) => a.number.localeCompare(b.number))

      return result
    }

    // Week and Month views use calendar_grid
    const calendarGrid = calendarData?.data?.calendar_grid || calendarData?.calendar_grid

    if (!calendarGrid) {
      return []
    }

    const assetMap = new Map()

    if (viewType === "month") {
      calendarGrid.forEach((dayData) => processMonthDay(assetMap, dayData))
    } else {
      calendarGrid.forEach((dayData) => processWeekDay(assetMap, dayData))
    }

    // Convert map to array, remove bookingIds helper, and sort by asset number
    return Array.from(assetMap.values())
      .map((asset) => {
        // Remove the bookingIds Set (used only for deduplication)
        const { bookingIds, ...assetWithoutIds } = asset
        return assetWithoutIds
      })
      .sort((a, b) => a.number.localeCompare(b.number))
  }, [calendarData])



  // Get total bookings count from metadata or data
  const totalBookingsCount = calendarData?.metadata?.upcoming_bookings_count || calendarData?.total_bookings || 0

  return { assets, loading, totalBookingsCount, journalNoteDates }
}
